<template>
  <div class="community-page">
    <!-- Banner区域 -->
    <div class="banner">
      <div class="banner-content">
        <h1 class="title">社区交流</h1>
        <p class="subtitle">分享生活，交流心得</p>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="container">
      <!-- 筛选栏 -->
      <div class="filter-bar">
        <div class="filter-left">
          <div class="filter-tabs">
            <el-radio-group v-model="activeTab" size="large">
              <el-radio-button label="recommend">推荐</el-radio-button>
              <el-radio-button label="latest">最新</el-radio-button>
              <el-radio-button label="hot">热门</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        <div class="filter-right">
          <el-button type="primary" size="large" @click="showPostDialog">
            <el-icon><Edit /></el-icon>
            发布动态
          </el-button>
        </div>
      </div>

      <!-- 筛选标签 -->
      <div class="filter-tags">
        <el-tag
          v-for="tag in tags"
          :key="tag.value"
          :type="tag.type"
          effect="plain"
          class="tag-item"
          @click="filterByTag(tag)"
        >
          {{ tag.label }}
        </el-tag>
      </div>

      <!-- 动态列表 -->
      <div class="posts-list">
        <el-card v-for="post in posts" :key="post.id" class="post-card">
          <!-- 用户信息 -->
          <div class="post-header">
            <div class="user-info">
              <el-avatar :size="40" :src="post.avatar || '/default-avatar.png'" />
              <div class="user-meta">
                <span class="username">{{ post.username || '匿名用户' }}</span>
                <span class="post-time">{{ formatTime(post.createTime) }}</span>
              </div>
            </div>
            <el-dropdown>
              <el-icon class="more-icon"><More /></el-icon>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item>收藏</el-dropdown-item>
                  <el-dropdown-item>举报</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>

          <!-- 动态内容 -->
          <div class="post-content">
            <p class="post-text">{{ post.content }}</p>
            <!-- 图片网格 -->
            <div class="image-grid" v-if="post.images && post.images.length">
              <div 
                v-for="(img, index) in post.images" 
                :key="index" 
                class="image-wrapper"
                :class="{
                  'single-image': post.images.length === 1,
                  'double-image': post.images.length === 2,
                  'multi-image': post.images.length >= 3
                }"
              >
                <el-image
                  :src="img"
                  :preview-src-list="post.images"
                  fit="contain"
                  class="grid-image"
                  loading="lazy"
                />
              </div>
            </div>
            <!-- 话题标签 -->
            <div class="post-tags">
              <el-tag
                v-for="tag in post.tags"
                :key="tag"
                size="small"
                effect="plain"
              >
                # {{ tag }}
              </el-tag>
            </div>
          </div>

          <!-- 互动栏 -->
          <div class="post-actions">
            <div class="action-item" @click="handleLike(post)">
              <el-icon :class="{ 'is-active': post.isLiked }"><Star /></el-icon>
              <span>{{ post.likes }}</span>
            </div>
            <div class="action-item" @click="handleComment(post)">
              <el-icon><ChatDotRound /></el-icon>
              <span>{{ post.comments.length }}</span>
            </div>
            <div class="action-item">
              <el-icon><Share /></el-icon>
              <span>分享</span>
            </div>
          </div>

          <!-- 评论区 -->
          <div class="comments-section" v-if="post.showComments">
            <el-input
              v-model="commentText"
              placeholder="写下你的评论..."
              class="comment-input"
            >
              <template #append>
                <el-button type="primary" @click="submitComment(post)">发送</el-button>
              </template>
            </el-input>
            <div class="comments-list">
              <div v-for="comment in post.comments" :key="comment.id" class="comment-item">
                <el-avatar :size="32" :src="comment.avatar" />
                <div class="comment-content">
                  <div class="comment-user">{{ comment.username }}</div>
                  <div class="comment-text">{{ comment.content }}</div>
                  <div class="comment-meta">
                    <span>{{ comment.time }}</span>
                    <span class="reply-btn">回复</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 加载更多 -->
      <div class="load-more">
        <el-button :loading="loading" @click="loadMore">加载更多</el-button>
      </div>
    </div>

    <!-- 发布动态对话框 -->
    <el-dialog
      v-model="postDialogVisible"
      title="发布动态"
      width="50%"
    >
      <el-form :model="newPost" ref="postForm" :rules="postRules">
        <el-form-item prop="content">
          <el-input
            v-model="newPost.content"
            type="textarea"
            :rows="4"
            placeholder="分享你的想法..."
          />
        </el-form-item>
        <el-form-item>
          <el-upload
            action="#"
            list-type="picture-card"
            :auto-upload="false"
            :on-change="handleImageChange"
            :on-remove="handleImageRemove"
            :http-request="customUpload"
            multiple
          >
            <el-icon><Plus /></el-icon>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="newPost.tags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="添加话题标签"
          >
            <el-option
              v-for="tag in commonTags"
              :key="tag"
              :label="tag"
              :value="tag"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="postDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitPost">
            发布
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { Edit, More, Star, ChatDotRound, Share, Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'

export default {
  name: 'Community',
  components: {
    Edit,
    More,
    Star,
    ChatDotRound,
    Share,
    Plus
  },
  setup() {
    const userStore = useUserStore()
    const activeTab = ref('recommend')
    const loading = ref(false)
    const postDialogVisible = ref(false)
    const commentText = ref('')
    const posts = ref([])
    const currentPage = ref(1)
    const pageSize = ref(10)
    const hasMore = ref(true)

    // 话题标签
    const tags = ref([
      { label: '全部', value: 'all', type: '' },
      { label: '生活', value: 'life', type: 'success' },
      { label: '美食', value: 'food', type: 'warning' },
      { label: '旅行', value: 'travel', type: 'danger' },
      { label: '电影', value: 'movie', type: 'info' }
    ])

    // 常用话题标签
    const commonTags = ref([
      '生活日常',
      '美食探店',
      '旅行日记',
      '电影推荐',
      '读书笔记'
    ])

    // 新动态
    const newPost = ref({
      content: '',
      images: [],
      tags: []
    })

    // OSS上传配置
    const uploadConfig = ref({
      host: '',
      dir: '',
      policy: '',
      signature: '',
      accessKeyId: '',
      expire: 0
    })

    // 计算上传参数
    const uploadData = computed(() => {
      if (!uploadConfig.value.host) return {}

      return {
        key: `${uploadConfig.value.dir}${Date.now()}-${Math.random().toString(36).slice(-6)}.jpg`,
        policy: uploadConfig.value.policy,
        OSSAccessKeyId: uploadConfig.value.accessKeyId,
        signature: uploadConfig.value.signature,
        success_action_status: '200',
        'Content-Type': 'image/jpeg',
        'x-oss-object-acl': 'public-read'
      }
    })

    // 获取动态列表
    const getPosts = async () => {
      try {
        loading.value = true
        const data = await userStore.fetchPosts({
          page: currentPage.value,
          pageSize: pageSize.value,
          tab: activeTab.value
        })

        if (currentPage.value === 1) {
          posts.value = data.posts
        } else {
          posts.value = [...posts.value, ...data.posts]
        }

        hasMore.value = data.hasMore
        currentPage.value++
      } catch (error) {
        ElMessage.error(error.message)
      } finally {
        loading.value = false
      }
    }

    // 获取OSS上传签名
    const getOssPolicy = async () => {
      try {
        const res = await userStore.getOssPolicy()
        if (!res.host) {
          throw new Error('获取的上传配置不完整')
        }
        uploadConfig.value = res
      } catch (error) {
        console.error('获取OSS配置失败:', error)
        ElMessage.error(error.message || '获取上传配置失败')
        throw error
      }
    }

    // 自定义上传方法
    const customUpload = async ({ file }) => {
      try {
        // 检查文件类型和大小
        const isValidType = ['image/jpeg', 'image/png'].includes(file.type)
        const isValidSize = file.size / 1024 / 1024 < 5

        if (!isValidType) {
          ElMessage.error('只能上传 JPG/PNG 格式的图片!')
          return
        }

        if (!isValidSize) {
          ElMessage.error('图片大小不能超过 5MB!')
          return
        }

        // 获取上传配置
        await getOssPolicy()

        // 创建表单数据
        const formData = new FormData()
        Object.entries(uploadData.value).forEach(([key, value]) => {
          formData.append(key, value)
        })
        formData.append('file', file)

        console.log('上传图片的数据:', {
          host: uploadConfig.value.host,
          formData: Object.fromEntries(formData.entries())
        })

        // 上传到OSS
        const response = await fetch(uploadConfig.value.host, {
          method: 'POST',
          body: formData
        })

        if (response.status === 200) {
          const fileUrl = `${uploadConfig.value.host}/${uploadData.value.key}`
          newPost.value.images.push(fileUrl)
          ElMessage.success('图片上传成功')
        } else {
          throw new Error('图片上传失败')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        ElMessage.error('图片上传失败，请重试')
      }
    }

    // 处理图片变化
    const handleImageChange = (file) => {
      if (file.status === 'ready') {
        customUpload({ file: file.raw })
      }
    }

    // 处理图片移除
    const handleImageRemove = (file) => {
      const index = newPost.value.images.findIndex(img => img.includes(file.url))
      if (index !== -1) {
        newPost.value.images.splice(index, 1)
      }
    }

    // 发布新动态
    const submitPost = async () => {
      try {
        if (!newPost.value.content) {
          ElMessage.warning('请输入内容')
          return
        }

        console.log('提交的数据:', {
          content: newPost.value.content,
          images: newPost.value.images,
          tags: newPost.value.tags
        })

        // 直接提交对象数据
        await userStore.createPost({
          content: newPost.value.content,
          images: newPost.value.images,
          tags: newPost.value.tags
        })

        ElMessage.success('发布成功')
        postDialogVisible.value = false
        currentPage.value = 1
        await getPosts()
        
        // 重置表单
        newPost.value = {
          content: '',
          images: [],
          tags: []
        }
      } catch (error) {
        console.error('发布失败:', error)
        ElMessage.error(error.message || '发布失败')
      }
    }

    // 点赞功能
    const handleLike = async (post) => {
      try {
        await userStore.toggleLike(post.id)
        post.isLiked = !post.isLiked
        post.likes += post.isLiked ? 1 : -1
      } catch (error) {
        ElMessage.error(error.message)
      }
    }

    // 发表评论
    const submitComment = async (post) => {
      if (!commentText.value.trim()) {
        ElMessage.warning('请输入评论内容')
        return
      }

      try {
        const newComment = await userStore.createComment(post.id, commentText.value)
        post.comments.unshift(newComment)
        commentText.value = ''
        ElMessage.success('评论成功')
      } catch (error) {
        ElMessage.error(error.message)
      }
    }

    // 根据标签筛选
    const filterByTag = async (tag) => {
      try {
        currentPage.value = 1
        loading.value = true
        const data = await userStore.fetchPosts({
          page: 1,
          pageSize: pageSize.value,
          tag: tag.value
        })
        posts.value = data.posts
        hasMore.value = data.hasMore
      } catch (error) {
        ElMessage.error(error.message)
      } finally {
        loading.value = false
      }
    }

    // 加载更多
    const loadMore = () => {
      if (!loading.value && hasMore.value) {
        getPosts()
      }
    }

    // 监听标签切换
    watch(activeTab, () => {
      currentPage.value = 1
      getPosts()
    })

    // 初始加载
    onMounted(() => {
      getPosts()
    })

    const showPostDialog = () => {
      postDialogVisible.value = true
    }

    const handleComment = (post) => {
      post.showComments = !post.showComments
    }

    // 添加时间格式化方法
    const formatTime = (time) => {
      if (!time) return '';
      const date = new Date(time);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return {
      activeTab,
      tags,
      posts,
      loading,
      postDialogVisible,
      newPost,
      commonTags,
      commentText,
      hasMore,
      showPostDialog,
      handleImageChange,
      submitPost,
      handleLike,
      handleComment,
      filterByTag,
      loadMore,
      submitComment,
      formatTime,
      uploadConfig,
      uploadData,
      handleImageRemove
    }
  }
}
</script>

<style scoped>
.title {
  font-size: 48px;
  margin: 10px 0; /* 调整上下间距 */
  padding-top: 30px;
  font-weight: 600; /* 加粗标题 */
}
.subtitle {
  font-size: 24px;
  opacity: 0.9; /* 稍微降低透明度 */
}
.banner {
  height: 300px;
  background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3');
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -60px;
}

.banner-content {
  text-align: center;
  color: white;
  padding-top: 60px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.filter-bar {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.filter-tabs {
  margin-bottom: 15px;
}

.filter-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.tag-item {
  cursor: pointer;
}

.post-card {
  margin-bottom: 20px;
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-meta {
  display: flex;
  flex-direction: column;
}

.username {
  font-weight: bold;
  color: #333;
}

.post-time {
  font-size: 12px;
  color: #999;
}

.more-icon {
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
}

.more-icon:hover {
  background: #f5f5f5;
}

.post-content {
  margin-bottom: 15px;
}

.post-text {
  margin-bottom: 15px;
  line-height: 1.6;
  color: #333;
}

.image-grid {
  display: grid;
  gap: 8px;
  margin: 15px 0;
}

.image-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 宽高比 */
  overflow: hidden;
  border-radius: 8px;
  background-color: #f5f5f5;
}

/* 单张图片样式 */
.single-image {
  padding-bottom: 56.25%; /* 16:9 宽高比 */
  grid-column: span 3;
}

/* 两张图片样式 */
.double-image {
  padding-bottom: 100%;
}

/* 多张图片样式 */
.multi-image {
  padding-bottom: 100%;
}

.grid-image {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  background-color: #f5f5f5;
}

/* 根据图片数量调整布局 */
.image-grid:has(.single-image) {
  grid-template-columns: 1fr;
  max-width: 600px;
}

.image-grid:has(.double-image) {
  grid-template-columns: repeat(2, 1fr);
}

.image-grid:has(.multi-image) {
  grid-template-columns: repeat(3, 1fr);
}

/* 响应式布局 */
@media (max-width: 768px) {
  .image-grid {
    gap: 4px;
  }

  .single-image {
    padding-bottom: 75%; /* 4:3 宽高比，更适合移动端 */
  }

  .image-grid:has(.multi-image) {
    grid-template-columns: repeat(2, 1fr); /* 移动端两列布局 */
  }
}

.post-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
}

.post-actions {
  display: flex;
  gap: 30px;
  padding: 10px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  cursor: pointer;
}

.action-item:hover {
  color: #409eff;
}

.is-active {
  color: #409eff;
}

.comments-section {
  margin-top: 15px;
  padding-top: 15px;
}

.comment-input {
  margin-bottom: 15px;
}

.comment-item {
  display: flex;
  gap: 12px;
  margin-bottom: 15px;
}

.comment-content {
  flex: 1;
}

.comment-user {
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
}

.comment-text {
  color: #666;
  line-height: 1.4;
}

.comment-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 15px;
}

.reply-btn {
  color: #409eff;
  cursor: pointer;
}

.load-more {
  text-align: center;
  margin-top: 30px;
}

@media (max-width: 768px) {
  .banner {
    height: 200px;
  }

  .container {
    padding: 15px;
  }

  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
